/**
 * Modal con el seguimiento de una recogida en MSoft
 * Permite además forzar la sincronización bajo demanda
 */
import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    Stack,
    Divider,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SyncIcon from "@mui/icons-material/Sync";
import EstadoBadge from "../ui/EstadoBadge";
import IncidenciaRecogida from "../ui/IncidenciaRecogida";
import { RECOGIDA_ESTADOS_SINCRONIZABLES } from "../../constants";
import { formatearFechaHora } from "../../helpers/fechas";

/**
 * Fila etiqueta / valor del bloque de seguimiento
 */
const Dato = ({ label, children }) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "flex-start",
            gap: { xs: 0.25, sm: 1 }
        }}
    >
        <Typography
            sx={{
                fontSize: { xs: 11, sm: 12 },
                fontWeight: 600,
                color: "#6c757d",
                minWidth: { xs: "auto", sm: 150 }
            }}
        >
            {label}:
        </Typography>
        <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: "#212529", flex: 1 }}>
            {children}
        </Typography>
    </Box>
);

/**
 * @param {boolean} show - Visibilidad del modal
 * @param {Function} onHide - Cerrar
 * @param {Object} recogida - Recogida con su bloque seguimiento
 * @param {Function} onSincronizar - Callback para forzar la sincronización
 * @param {boolean} sincronizando - Sincronización en curso
 */
const ModalSeguimiento = ({ show, onHide, recogida, onSincronizar, sincronizando = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const seguimiento = recogida?.seguimiento;
    const transportista = seguimiento?.transportista;
    const incidencias = seguimiento?.incidencias || [];

    // Recién creada la recogida el seguimiento viene a null: aún no se ha sincronizado
    const sinDatos = !seguimiento || !seguimiento.ultimaSincronizacion;
    const puedeSincronizar = RECOGIDA_ESTADOS_SINCRONIZABLES.includes(recogida?.estado);

    return (
        <Dialog
            open={show}
            onClose={sincronizando ? undefined : onHide}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    m: isMobile ? 0 : 2
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                        }}
                    >
                        <LocalShippingIcon sx={{ color: "#cc071e", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, color: "#212529", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
                        >
                            Seguimiento
                        </Typography>
                        {recogida?.numeroTicket && (
                            <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: "#6c757d" }}>
                                {recogida.numeroTicket}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <IconButton onClick={onHide} disabled={sincronizando} size="small" sx={{ color: "#6c757d" }}>
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Content */}
            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : "initial"
                }}
            >
                {sinDatos ? (
                    <Alert severity="info" sx={{ fontSize: { xs: 12, sm: 13 } }}>
                        Todavía no se ha sincronizado con MSoft, así que no hay datos de seguimiento.
                        {puedeSincronizar && ' Puedes forzarlo con el botón de abajo.'}
                    </Alert>
                ) : (
                    <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
                        <Dato label="Estado">
                            <EstadoBadge estado={recogida.estado} />
                        </Dato>
                        <Dato label="Situación MSoft">{seguimiento.situacionMsoft || '—'}</Dato>
                        {/* El número de MSoft va en la raíz de la recogida, no en el seguimiento */}
                        <Dato label="Nº recogida MSoft">{recogida.idRecogidaMsoft || '—'}</Dato>
                        <Dato label="Solicitada por">{recogida.usuario?.username || '—'}</Dato>

                        <Divider />

                        {/* La tabla solo muestra la modificación, que es por lo que filtran
                            las fechas; la creación se consulta aquí */}
                        <Dato label="Creada">{formatearFechaHora(recogida.creado)}</Dato>
                        <Dato label="Modificada">{formatearFechaHora(recogida.modificado)}</Dato>

                        <Divider />

                        <Dato label="Alta en MSoft">{formatearFechaHora(seguimiento.fechaAltaMsoft)}</Dato>
                        <Dato label="Fecha acordada">{formatearFechaHora(seguimiento.fechaAcordada)}</Dato>
                        <Dato label="Fecha asignada">{formatearFechaHora(seguimiento.fechaAsignada)}</Dato>
                        <Dato label="Fecha real">{formatearFechaHora(seguimiento.fechaReal)}</Dato>

                        <Divider />

                        <Dato label="Transportista">
                            {transportista?.nombre
                                ? `${transportista.nombre}${transportista.vehiculo ? ` (${transportista.vehiculo})` : ''}`
                                : '—'}
                        </Dato>
                        <Dato label="Área">{seguimiento.area?.descripcion || '—'}</Dato>

                        {incidencias.length > 0 && (
                            <>
                                <Divider />
                                <Box>
                                    <Typography
                                        sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600, color: "#6c757d", mb: 0.5 }}
                                    >
                                        Incidencias ({incidencias.length}):
                                    </Typography>
                                    <Stack spacing={1}>
                                        {incidencias.map((incidencia, i) => (
                                            <IncidenciaRecogida key={i} incidencia={incidencia} />
                                        ))}
                                    </Stack>
                                </Box>
                            </>
                        )}

                        <Divider />

                        <Dato label="Última sincronización">
                            {formatearFechaHora(seguimiento.ultimaSincronizacion)}
                        </Dato>
                        <Dato label="Intentos">{seguimiento.intentosSincronizacion ?? 0}</Dato>
                    </Stack>
                )}
            </DialogContent>

            <Divider />

            {/* Actions */}
            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column-reverse", sm: "row" }
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onHide}
                    disabled={sincronizando}
                    fullWidth={isMobile}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        color: "#6c757d",
                        borderColor: "#6c757d",
                        "&:hover": { borderColor: "#495057", backgroundColor: "rgba(0,0,0,0.04)" }
                    }}
                >
                    Cerrar
                </Button>

                {/* Solo CONFIRMADA, ASIGNADA y EN_CURSO admiten sincronización */}
                {puedeSincronizar && (
                    <Button
                        variant="contained"
                        onClick={onSincronizar}
                        disabled={sincronizando}
                        fullWidth={isMobile}
                        startIcon={
                            sincronizando
                                ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                                : <SyncIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        }
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            px: { xs: 2, sm: 2.5 },
                            py: { xs: 1.25, sm: 1 },
                            fontSize: { xs: 14, sm: 15 },
                            backgroundColor: "#cc071e",
                            "&:hover": { backgroundColor: "#a30618" },
                            "&:disabled": { backgroundColor: "#6c757d", color: "#fff" }
                        }}
                    >
                        {sincronizando ? "Sincronizando..." : "Sincronizar"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ModalSeguimiento;
